import { Anchor, Button, Group, Modal, Stack } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { useState } from 'react';
import { z } from 'zod';

import { Declaration, DeclarationGoods } from '@e-origin/shared';

import { NumberInput } from '@components';
import { MANUALLY_SET_PRICE_MIN_VALUE } from '@constants';
import { useManuallySetPrice } from '@hooks/declaration';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';

interface ManuallySetPriceProps {
  goodDetails: DeclarationGoods;
  declaration: Declaration;
  opened: boolean;
  onHide(newPrice?: number): void;
}

const priceSchema = z.object({
  price: z.number().min(MANUALLY_SET_PRICE_MIN_VALUE, { message: 'Price is required' }),
});

type PriceFormValues = z.infer<typeof priceSchema>;

export const ManuallySetPrice: React.FC<ManuallySetPriceProps> = (props) => {
  const { goodDetails, declaration, opened, onHide } = props;

  const [submitting, setSubmitting] = useState(false);
  const setPriceMutation = useManuallySetPrice();

  const form = useFormWithNameInputProp<PriceFormValues>({
    initialValues: {
      price: 0,
    },
    validate: zodResolver(priceSchema),
    validateInputOnBlur: true,
  });

  const submitPrice = async (values: PriceFormValues) => {
    setSubmitting(true);
    if (declaration?._id && goodDetails?._id) {
      const webUrl = goodDetails.webScraping?.webUrl;
      const goodItemsIds = declaration.goodsShipmentItems.filter((good) => good.webScraping?.webUrl === webUrl).map((good) => good._id);

      await setPriceMutation.mutateAsync({
        id: declaration._id,
        goodItemsIds,
        newPrice: values.price,
      });

      setSubmitting(false);
      onHide(values.price);
    }
  };

  return (
    <Modal title="Manually set price" opened={opened} onClose={() => onHide()} zIndex={9999} centered>
      <form onSubmit={form.onSubmit(submitPrice)}>
        <Stack gap="sm" mb="xl">
          <div>
            Automatic risk analysis wasn't able to extract the price form the website:{' '}
            <Anchor href={`${goodDetails.webScraping.webUrl}`} rel="noreferrer" target="_blank" underline="hover" c={'#0F6298'}>
              {goodDetails.webScraping.webUrl}
            </Anchor>
          </div>
          <div>You can manually set the web value below</div>

          <NumberInput
            data-pw="manually-set-price-input"
            {...form.getInputProps('price')}
            label="Web value"
            placeholder="Web value"
            disabled={submitting}
            size="lg"
          />
        </Stack>

        <Group justify="center">
          <Button type="button" variant="transparent" onClick={() => onHide(form.values.price)} disabled={submitting} size="lg">
            Cancel
          </Button>

          <Button data-pw="submit-btn" type="submit" loading={submitting} disabled={!form.isValid()} variant="filled" size="lg">
            Confirm
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
